import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  IconButton,
  Grid,
  Chip,
  Paper,
  Button,
  MenuItem,
  Select,
  TextField,
  Checkbox,
  FormControl,
  InputLabel,
  FormControlLabel,
  InputAdornment,
  Box,
  Tooltip,
  Typography
} from '@mui/material';
import isEqual from 'lodash/isEqual';
import AdvancedSearch from './advancedSearch.component';
import { useMoneyInputs } from 'modules/common/useMoneyInputs';

const MultiRowForm = ({
  formValues = [],
  saveFunction,
  formFields,
  createNewRowFunc,
  addRowLabel,
  isDisabled = false,
  header,
  headerCheckbox,
  addButtons,
  parentValues = {}
}) => {
  const moneyInput = useMoneyInputs();
  const [formValuesState, setFormValuesState] = useState(formValues);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    setFormValuesState([...formValues]);
    setHasChanges(false);
  }, [formValues]);

  const checkForChanges = updatedValues => {
    setHasChanges(!isEqual(updatedValues, formValues));
  };

  const addFormRow = () => {
    const updatedValues = [...formValuesState, createNewRowFunc()];
    setFormValuesState(updatedValues);
    checkForChanges(updatedValues);
  };

    const addFormRowMultiButton = (button) => {
    const updatedValues = [...formValuesState, button.addFormRow()];
    setFormValuesState(updatedValues);
    checkForChanges(updatedValues);
  };

  const removeFormRow = index => {
    const updatedValues = formValuesState.filter((_, i) => i !== index);
    setFormValuesState(updatedValues);
    checkForChanges(updatedValues);
  };

  const handleFieldChange = (value, index, field) => {
    const updatedValues = [...formValuesState];
    updatedValues[index] = { ...updatedValues[index], [field]: value };
    setFormValuesState(updatedValues);
    checkForChanges(updatedValues);
  };

  const parseValueToArray = input => {
    if (!input) return [];
    if (Array.isArray(input)) return input;
    if (typeof input === 'string') {
      return input
        .split(/[;,]/)
        .map(v => v.trim())
        .filter(Boolean);
    }
    return [input];
  };

  const getOptionId = option => {
    if (!option) return null;
    return option.id ?? option.value ?? option.clientDivisionId ?? option.relocationPolicyId ?? option.policyId ?? null;
  };

  const getOptionLabel = option => {
    if (!option) return '';
    return option.title ?? option.label ?? option.name ?? option.divisionName ?? option.policyDescription ?? '';
  };

  const normalizeMultiSelectValue = (field, rowValues) => {
    const options = resolveFieldOptions(field, rowValues);
    const selectedRaw = parseValueToArray(rowValues?.[field.name]);

    return selectedRaw
      .map(selected => {
        if (selected && typeof selected === 'object') {
          const selectedId = getOptionId(selected);
          if (selectedId === null) return null;

          const matchedOption = options.find(opt => String(getOptionId(opt)) === String(selectedId));
          if (matchedOption) {
            return matchedOption;
          }

          return {
            id: selectedId,
            title: getOptionLabel(selected)
          };
        }

        const matchedOption = options.find(opt => String(getOptionId(opt)) === String(selected));
        if (matchedOption) {
          return matchedOption;
        }

        // Keep displayable fallback when options are not yet loaded/resolved.
        return {
          id: selected,
          title: String(selected)
        };
      })
      .filter(Boolean);
  };

  const saveForm = async () => {
    if (saveFunction) {
      setIsSubmitting(true);
      await saveFunction(formValuesState);
      setIsSubmitting(false);
      setHasChanges(false);
    }
  };

  const shouldRenderField = (field, rowValues) => {
    if (field.type === 'hidden') {
      return false;
    }

    if (!field.dependentOn) {
      return true;
    }

    const rowHasDependencyKey = Object.prototype.hasOwnProperty.call(rowValues || {}, field.dependentOn);
    const dependentValue = rowHasDependencyKey ? rowValues[field.dependentOn] : parentValues[field.dependentOn];

    if (Array.isArray(field.dependentValue)) {
      return field.dependentValue.includes(dependentValue);
    }

    return dependentValue === field.dependentValue;
  };

  const resolveFieldOptions = (field, rowValues) => {
    const rawOptions = typeof field.options === 'function' ? field.options(rowValues || {}) : field.options;
    return Array.isArray(rawOptions) ? rawOptions : [];
  };

  const renderHeader = () => {
    if (!header) return null;
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center'
        }}
      >
        {header.iconPath && (
          <Tooltip title={header.title} placement="right">
            <FontAwesomeIcon icon={header.iconPath} size="2x" />
          </Tooltip>
        )}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '20px',
            paddingLeft: header.iconPath ? '20px' : '0px'
          }}
        >
          <Typography
            variant="h5"
            sx={{
              fontSize: '1.3125rem',
              fontWeight: 'bold'
            }}
          >
            {header.title}
          </Typography>
        </Box>
        {headerCheckbox && headerCheckbox()}
      </Box>
    );
  };

  return (
    <Grid item xs={12}>
      <Paper className="card-root" style={{ padding: '16px' }}>
        <Grid container item alignItems="center" justifyContent="space-between" style={{ marginBottom: '16px' }}>
          {renderHeader()}
          {addButtons && addButtons.length > 1 ? addButtons.map((button) => (
            <Button disabled={isSubmitting || isDisabled} onClick={() => addFormRowMultiButton(button)}>
              {button.addRowLabel}
            </Button>
          )) : 
            <Button disabled={isSubmitting || isDisabled} onClick={addFormRow}>
              {addRowLabel}
            </Button>
            }
          {header.hint && (
            <Typography variant="caption" display="block" color="text.secondary" sx={{ marginTop: '4px' }}>
              {header.hint}
            </Typography>
          )}
          
        </Grid>
        {formValuesState.map((formValue, index) => {
          if(addButtons && addButtons.length > 1) {
            // if multiple add buttons, determine which fields apply to each row added
            //const visibleFields = formFields.filter(field => field.type !== 'hidden');

            const visibleFields =
              addButtons
                .find((button) => button.categoryType === formValue.categoryType)
                ?.formFields.filter(field => shouldRenderField(field, formValue)) || [];
            const fieldCount = visibleFields.length;
            const xsValue = Math.floor(12 / fieldCount);
          
            return (
              <Grid container key={index} alignItems="center" spacing={2} wrap="nowrap" style={{ marginBottom: '8px', width: '100%', overflowX: 'auto' }}>
                {visibleFields.map(field => {
                  const resolvedFieldType =
                    field.type === 'dependency-value'
                      ? (resolveFieldOptions(field, formValue).length > 0 ? 'multi-select-v2' : 'text')
                      : field.type;

                  if (resolvedFieldType === 'number') {
                    return (
                      <Grid item xs={xsValue} key={field.name}>
                        <TextField
                          label={field.label}
                          value={formValue[field.name]}
                          onChange={e => handleFieldChange(e.target.value, index, field.name)}
                          disabled={isSubmitting || isDisabled}
                          fullWidth
                          type="number"
                          margin="dense"
                        />
                      </Grid>
                    );
                  }
                  if (resolvedFieldType === 'select') {
                    const options = resolveFieldOptions(field, formValue);
                    return (
                      <Grid item xs={xsValue} key={field.name}>
                        <FormControl fullWidth margin="dense">
                          <InputLabel>{field.label}</InputLabel>
                          <Select
                            value={formValue[field.name]}
                            onChange={e => handleFieldChange(e.target.value, index, field.name)}
                            disabled={isSubmitting || isDisabled}
                          >
                            {options.map(option => (
                              <MenuItem key={option.value} value={option.value}>
                                {option.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                    );
                  }
                  if (resolvedFieldType === 'text') {
                    return (
                      <Grid item xs={xsValue} key={field.name}>
                        <TextField
                          label={field.label}
                          value={formValue[field.name]}
                          onChange={e => handleFieldChange(e.target.value, index, field.name)}
                          disabled={isSubmitting || isDisabled}
                          fullWidth
                          margin="dense"
                        />
                        {field.hint && (
                          <Typography variant="caption" display="block" color="text.secondary" sx={{ marginTop: '4px' }}>
                            {field.hint}
                          </Typography>
                        )}
                      </Grid>
                    );
                  }

                  if(resolvedFieldType === 'dropdown-v2') {
                    const options = resolveFieldOptions(field, formValue);
                    return (
                      <Grid item xs={xsValue} key={field.name}>
                        <FormControl fullWidth margin="dense">
                          <InputLabel id={field.header}>
                          {field.label}
                        </InputLabel>
                        <Select
                          labelId={field.label}
                          key={field.name}
                          disabled={field.disabled}
                          label={field.label}
                          name={field.name}
                          required={field.required}
                          margin="dense"
                          sx={{ marginRight: '6px' }}
                          value={formValue[field.name]}
                          onChange={e => handleFieldChange(e.target.value, index, field.name)}
                          variant="standard"
                        >
                          {options.map((opt) => (
                            <MenuItem key={opt.id} value={opt.id}>
                              {opt.title}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      </Grid>
                    )}
                  if (resolvedFieldType === 'multi-select-v2') {
                    const options = resolveFieldOptions(field, formValue);
                    const selectedValues = normalizeMultiSelectValue(field, formValue);
                        return (
                          <Grid item xs={xsValue} key={field.name}>
                          <div style={{ width: '100%' }}>
                            <FormControl fullWidth margin="dense" sx={{ marginTop: '6px', marginBottom: '6px', minWidth: 0 }}>
                              <InputLabel sx={{ marginLeft: '-15px', marginTop: '7px' }} id={field.header}>
                                {field.header}
                              </InputLabel>
                              <Select
                                labelId={field.header}
                                key={field.name}
                                label={field.label}
                                name={field.name}
                                required={field.required}
                                disabled={field.disabled}
                                value={selectedValues}
                                renderValue={selected => (
                                  <div
                                    style={{
                                      display: 'flex',
                                      flexWrap: 'wrap',
                                      gap: '4px'
                                    }}
                                  >
                                    {selected.map(value => (
                                      <Chip
                                        key={getOptionId(value)}
                                        label={getOptionLabel(value)}
                                        onDelete={() => {
                                          const updatedValues = selectedValues.filter(v => String(getOptionId(v)) !== String(getOptionId(value)));
                                          handleFieldChange(updatedValues, index, field.name);
                                        }}
                                        onMouseDown={e => {
                                          e.stopPropagation();
                                        }}
                                      />
                                    ))}
                                  </div>
                                )}
                                multiple
                                margin="dense"
                                variant="standard"
                                sx={{ width: '100%', minWidth: 0 }}
                                onChange={e => handleFieldChange(e.target.value, index, field.name)}
                              >
                                {options.map(opt => (
                                  <MenuItem key={opt.id} value={opt} sx={{ margin: 'dense' }}>
                                    {opt.title}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                            {field.required && (
                              <>
                                <Typography variant="caption" display="block" color="primary">
                                  Required
                                </Typography>
                              </>
                            )}
                          </div>
                          </Grid>
                        );
                      }  
                  if (resolvedFieldType === 'checkbox') {
                    return (
                      <Grid item xs={xsValue} key={field.name}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={formValue[field.name]}
                              onChange={() => handleFieldChange(!formValue[field.name], index, field.name)}
                              disabled={isSubmitting || isDisabled}
                            />
                          }
                          label={field.label}
                        />
                      </Grid>
                    );
                  }
                  return null;
                })}
                <Grid item xs="auto">
                  <IconButton onClick={() => removeFormRow(index)} disabled={isSubmitting || isDisabled}>
                    <FontAwesomeIcon icon={['far', 'trash-alt']} size="lg" />
                  </IconButton>
                </Grid>
              </Grid>
            );
          
          }
          else {
          const visibleFields = formFields.filter(field => shouldRenderField(field, formValue));
          const fieldCount = visibleFields.length;
          const xsValue = Math.floor(12 / fieldCount);

          return (
            <Grid container key={index} alignItems="center" spacing={2} wrap="nowrap" style={{ marginBottom: '8px', width: '100%', overflowX: 'auto' }}>
              {visibleFields.map(field => {
                const resolvedFieldType =
                  field.type === 'dependency-value'
                    ? (resolveFieldOptions(field, formValue).length > 0 ? 'multi-select-v2' : 'text')
                    : field.type;

                if (resolvedFieldType === 'select') {
                  const options = resolveFieldOptions(field, formValue);
                  return (
                    <Grid item xs={xsValue} key={field.name}>
                      <FormControl fullWidth margin="dense">
                        <InputLabel>{field.label}</InputLabel>
                        <Select
                          value={formValue[field.name]}
                          onChange={e => handleFieldChange(e.target.value, index, field.name)}
                          disabled={isSubmitting || isDisabled}
                        >
                          {options.map(option => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  );
                }
                 if (resolvedFieldType === 'number') {
                    return (
                      <Grid item xs={xsValue} key={field.name}>
                        <TextField
                          label={field.label}
                          value={formValue[field.name]}
                          onChange={e => handleFieldChange(e.target.value, index, field.name)}
                          disabled={isSubmitting || isDisabled}
                          fullWidth
                          type="number"
                          margin="dense"
                        />
                      </Grid>
                    );
                  }
                   if (resolvedFieldType === 'money') {
                    return (
                      <Grid item xs={xsValue} key={field.name}>
                        <TextField
                          label={field.label}
                          {...moneyInput(`${index}.${field.name}`, formValue[field.name], e =>
                            handleFieldChange(e.target.value, index, field.name),
                          )}
                          disabled={isSubmitting || isDisabled}
                          fullWidth
                          type="number"
                          margin="dense"
                          InputProps={{
                            startAdornment: <InputAdornment position="start">$</InputAdornment>,
                          }}
                        />
                      </Grid>
                    );
                  }
                if (resolvedFieldType === 'text') {
                  return (
                    <Grid item xs={xsValue} key={field.name}>
                      <TextField
                        label={field.label}
                        value={formValue[field.name]}
                        onChange={e => handleFieldChange(e.target.value, index, field.name)}
                        disabled={isSubmitting || isDisabled}
                        fullWidth
                        margin="dense"
                      />
                      {field.hint && (
                          <Typography variant="caption" display="block" color="text.secondary" sx={{ marginTop: '4px' }}>
                            {field.hint}
                          </Typography>
                        )}
                    </Grid>
                  );
                }
                if(resolvedFieldType === 'dropdown-v2') {
                  const options = resolveFieldOptions(field, formValue);
                    return (
                      <Grid item xs={xsValue} key={field.name}>
                        <FormControl fullWidth margin="dense">
                          <InputLabel id={field.header}>
                          {field.label}
                        </InputLabel>
                        <Select
                          labelId={field.label}
                          key={field.name}
                          disabled={field.disabled}
                          label={field.label}
                          name={field.name}
                          required={field.required}
                          margin="dense"
                          sx={{ marginRight: '6px' }}
                          value={formValue[field.name]}
                          onChange={e => handleFieldChange(e.target.value, index, field.name)}
                          variant="standard"
                        >
                          {options.map((opt) => (
                            <MenuItem key={opt.id} value={opt.id}>
                              {opt.title}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      </Grid>
                    )}
                if (resolvedFieldType === 'multi-select-v2') {
                  const options = resolveFieldOptions(field, formValue);
                  const selectedValues = normalizeMultiSelectValue(field, formValue);
                        return (
                          <Grid item xs={xsValue} key={field.name}>
                          <div style={{ width: '100%' }}>
                            <FormControl fullWidth margin="dense" sx={{ marginTop: '6px', marginBottom: '6px', minWidth: 0 }}>
                              <InputLabel sx={{ marginLeft: '-15px', marginTop: '7px' }} id={field.label}>
                                {field.label}
                              </InputLabel>
                              <Select
                                labelId={field.label}
                                key={field.name}
                                label={field.label}
                                name={field.name}
                                required={field.required}
                                disabled={field.disabled}
                                value={selectedValues}
                                renderValue={selected => (
                                  <div
                                    style={{
                                      display: 'flex',
                                      flexWrap: 'wrap',
                                      gap: '4px'
                                    }}
                                  >
                                    {selected.map(value => (
                                      <Chip
                                        key={getOptionId(value)}
                                        label={getOptionLabel(value)}
                                        onDelete={() => {
                                          const updatedValues = selectedValues.filter(v => String(getOptionId(v)) !== String(getOptionId(value)));
                                          handleFieldChange(updatedValues, index, field.name);
                                        }}
                                        onMouseDown={e => {
                                          e.stopPropagation();
                                        }}
                                      />
                                    ))}
                                  </div>
                                )}
                                multiple
                                margin="dense"
                                variant="standard"
                                sx={{ width: '100%', minWidth: 0 }}
                                onChange={e => handleFieldChange(e.target.value, index, field.name)}
                              >
                                {options.map(opt => (
                                  <MenuItem key={opt.id} value={opt} sx={{ margin: 'dense' }}>
                                    {opt.title}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                            {field.required && (
                              <>
                                <Typography variant="caption" display="block" color="primary">
                                  Required
                                </Typography>
                              </>
                            )}
                          </div>
                          </Grid>
                        );
                      }  
                if(resolvedFieldType === 'advanced-search')
                {
                  return (
                    <Grid item xs={xsValue} key={field.name}>
                      <AdvancedSearch
                        labelText={field.label}
                        displayValueAccessorKey={field.searchInfo.nameKey}
                        saveIdValueAccessorKey={field.searchInfo.idKey}
                        searchingCriteria={field.searchInfo.searchingColumns}
                        searchingCriteriaExtensions={field.searchInfo.searchingColumnExtensions}
                        searchingRows={field.searchInfo.searchingRows}
                        id={formValue[field.name]}
                        value={formValue[field.displayKey]}
                        setIdValue={(id, value) => {
                          handleFieldChange(id, index, field.name);
                          handleFieldChange(value, index, field.displayKey);
                        }}
                      />
                      </Grid>
                  )
                }
                 if (resolvedFieldType === 'decimal') {
                      return (
                        <Grid item xs={xsValue} key={field.name}>
                          <TextField
                            key={field.name}
                            label={field.label}
                            name={field.name}
                            value={formValue[field.name]}
                            margin="dense"
                            disabled={field.disabled}
                            sx={{ maxWidth: '360px' }}
                            variant="standard"
                            type="number"
                            allowDecimal={true}
                            onChange={e => handleFieldChange(e.target.value, index, field.name)}
                          />
                        </Grid>
                      );
                    }
                if (resolvedFieldType === 'checkbox') {
                  return (
                    <Grid item xs={xsValue} key={field.name}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={formValue[field.name]}
                            onChange={() => handleFieldChange(!formValue[field.name], index, field.name)}
                            disabled={isSubmitting || isDisabled}
                          />
                        }
                        label={field.label}
                      />
                    </Grid>
                  );
                }
                return null;
              })}
              <Grid item xs="auto">
                <IconButton onClick={() => removeFormRow(index)} disabled={isSubmitting || isDisabled}>
                  <FontAwesomeIcon icon={['far', 'trash-alt']} size="lg" />
                </IconButton>
              </Grid>
            </Grid>
          );
          }

          
        })}
        <Grid container justifyContent="flex-end" style={{ marginTop: '1rem' }}>
          <Button
            color="secondary"
            variant="contained"
            sx={{ maxHeight: '40px', marginLeft: '12px', marginTop: '8px' }}
            onClick={saveForm}
            disabled={!hasChanges || isSubmitting || isDisabled}
          >
            Save
          </Button>
        </Grid>
      </Paper>
    </Grid>
  );
};

export default MultiRowForm;
